/**
 * AURA Content Effects
 * CSS injection for content adjustments and text magnifier implementation
 */

import { ContentSettings } from '../../core/types';
import { cssInjector } from '../../core/css-injector';
import { Logger } from '../../core/logger';

const logger = new Logger('ContentEffects');

const STYLE_IDS = {
  contentScaling: 'content-scaling',
  readableFont: 'readable-font',
  highlightTitles: 'highlight-titles',
  highlightLinks: 'highlight-links',
  fontSizing: 'font-sizing',
  lineHeight: 'line-height',
  letterSpacing: 'letter-spacing',
  textAlignment: 'text-alignment',
};

export { enableTextMagnifier, disableTextMagnifier, getTextAtPoint } from './magnifier';

const GOOGLE_FONTS_URL =
  'https://fonts.googleapis.com/css2?family=Atkinson+Hyperlegible:ital,wght@0,400;0,700;1,400;1,700&display=swap';
const FONT_LINK_ATTR = 'data-aura-font';

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

// ── Readable-font loading ──────────────────────────────────────────

function ensureReadableFontLoaded(): void {
  if (document.querySelector(`link[${FONT_LINK_ATTR}="readable"]`)) return;

  const target = document.head ?? document.documentElement;
  if (!target) {
    logger.warn('Cannot inject font link: no DOM target available');
    return;
  }
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = GOOGLE_FONTS_URL;
  link.setAttribute(FONT_LINK_ATTR, 'readable');
  target.appendChild(link);
  logger.debug('Atkinson Hyperlegible font stylesheet injected');
}

function removeReadableFontLink(): void {
  document.querySelector(`link[${FONT_LINK_ATTR}="readable"]`)?.remove();
}

// ── Style helpers (DRY rule builders) ──────────────────────────────

function buildReadableFontCss(): string {
  return `
    :where(html) body, :where(html) body * {
      font-family: 'Atkinson Hyperlegible', -apple-system, BlinkMacSystemFont,
        'Segoe UI', Roboto, sans-serif !important;
    }
    :where(html) body [class*="icon"],
    :where(html) body [class*="fa-"],
    :where(html) body [class*="material-icons"],
    :where(html) body i[class] {
      font-family: inherit !important;
    }
    [data-aura] { font-family: inherit !important; }
  `;
}

function buildTextAlignmentCss(alignment: string): string {
  return `
    :where(html) body p, :where(html) body li, :where(html) body td,
    :where(html) body th,
    :where(html) body div:not([class*="nav"]):not([class*="menu"]):not([class*="header"]):not([class*="footer"]):not([data-aura]) {
      text-align: ${alignment} !important;
    }
  `;
}

// ── Main style application ─────────────────────────────────────────

export function applyContentStyles(settings: ContentSettings): void {
  applyContentScaling(settings.contentScaling);
  applyFontSizing(settings.fontSizing);
  applyLineHeight(settings.lineHeight);
  applyLetterSpacing(settings.letterSpacing);
  applyReadableFont(settings.readableFont);
  applyHighlightTitles(settings.highlightTitles);
  applyHighlightLinks(settings.highlightLinks);
  applyTextAlignment(settings.textAlignment);
}

function applyContentScaling(value: number): void {
  if (value !== 100) {
    cssInjector.setStylesheet(
      STYLE_IDS.contentScaling,
      `:where(html) body { zoom: ${value / 100}; }`,
      10
    );
  } else {
    cssInjector.removeStylesheet(STYLE_IDS.contentScaling);
  }
}

function applyFontSizing(value: number): void {
  const fontSize = clamp(value, 12, 32);
  if (fontSize !== 16) {
    cssInjector.setStylesheet(
      STYLE_IDS.fontSizing,
      `
        :where(html) body { font-size: ${fontSize}px !important; }
        :where(html) body * { font-size: inherit; }
      `,
      20
    );
  } else {
    cssInjector.removeStylesheet(STYLE_IDS.fontSizing);
  }
}

function applyLineHeight(value: number): void {
  if (value !== 1.5) {
    cssInjector.setStylesheet(
      STYLE_IDS.lineHeight,
      `
        :where(html) body, :where(html) body p, :where(html) body li,
        :where(html) body td, :where(html) body th, :where(html) body span,
        :where(html) body div {
          line-height: ${value} !important;
        }
      `,
      21
    );
  } else {
    cssInjector.removeStylesheet(STYLE_IDS.lineHeight);
  }
}

function applyLetterSpacing(value: number): void {
  if (value !== 0) {
    cssInjector.setStylesheet(
      STYLE_IDS.letterSpacing,
      `
        :where(html) body, :where(html) body * {
          letter-spacing: ${value}px !important;
        }
      `,
      22
    );
  } else {
    cssInjector.removeStylesheet(STYLE_IDS.letterSpacing);
  }
}

function applyReadableFont(enabled: boolean): void {
  if (enabled) {
    ensureReadableFontLoaded();
    cssInjector.setStylesheet(STYLE_IDS.readableFont, buildReadableFontCss(), 15);
  } else {
    cssInjector.removeStylesheet(STYLE_IDS.readableFont);
    removeReadableFontLink();
  }
}

function applyHighlightTitles(enabled: boolean): void {
  if (enabled) {
    cssInjector.setStylesheet(
      STYLE_IDS.highlightTitles,
      `
        :where(html) body h1, :where(html) body h2, :where(html) body h3,
        :where(html) body h4, :where(html) body h5, :where(html) body h6,
        :where(html) body [role="heading"] {
          background-color: rgba(30, 58, 95, 0.1) !important;
          border-bottom: 2px solid #1e3a5f !important;
          padding: 4px 8px !important;
          display: inline-block !important;
        }
      `,
      30
    );
  } else {
    cssInjector.removeStylesheet(STYLE_IDS.highlightTitles);
  }
}

function applyHighlightLinks(enabled: boolean): void {
  if (enabled) {
    cssInjector.setStylesheet(
      STYLE_IDS.highlightLinks,
      `
        :where(html) body a[href] {
          text-decoration: underline !important;
          text-decoration-thickness: 2px !important;
          text-underline-offset: 3px !important;
          outline: 2px solid transparent !important;
          outline-offset: 2px !important;
          background-color: rgba(74, 144, 217, 0.1) !important;
          padding: 2px 4px !important;
          border-radius: 2px !important;
        }
        :where(html) body a[href]:hover,
        :where(html) body a[href]:focus {
          outline-color: #4a90d9 !important;
        }
      `,
      31
    );
  } else {
    cssInjector.removeStylesheet(STYLE_IDS.highlightLinks);
  }
}

function applyTextAlignment(alignment: string): void {
  if (alignment !== 'default') {
    cssInjector.setStylesheet(
      STYLE_IDS.textAlignment,
      buildTextAlignmentCss(alignment),
      23
    );
  } else {
    cssInjector.removeStylesheet(STYLE_IDS.textAlignment);
  }
}

